import React from "react";
import { Box, Paper, Typography, CircularProgress, Chip } from "@mui/material";
import noTableData from "../../assets/images/noTableData.svg";

const CHART_H = 220;
const PAD     = { top: 20, right: 20, bottom: 75, left: 48 };

const AI_COLOR   = "#6366F1";
const HIST_COLOR = "#94A3B8";

const buildChart = (data, chartW) => {
  const allVals = data.flatMap((d) => [d.aiPredicted, d.historicalAvg]);
  const maxVal  = Math.max(...allVals);
  const yMax    = Math.ceil((maxVal * 1.2) / 10) * 10 || 60;

  const plotW = chartW - PAD.left - PAD.right;
  const plotH = CHART_H - PAD.top  - PAD.bottom;
  const xStep = data.length > 1 ? plotW / (data.length - 1) : 0;

  const xOf      = (i) => PAD.left + i * xStep;
  const yOf      = (v) => PAD.top + plotH * (1 - v / yMax);
  const pointsOf = (key) => data.map((d, i) => `${xOf(i)},${yOf(d[key])}`).join(" ");

  const gridTicks = [0, 0.25, 0.5, 0.75, 1];

  return { xOf, yOf, pointsOf, yMax, gridTicks };
};

const PredictiveTimeToHire = ({ data, loading }) => {
  const hasData = data && data.length > 0;
  const chartWidth = Math.max(500, (data?.length || 0) * 40);

  return (
    <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, p: 2.5, height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Predictive Time-to-Hire
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.25}>
            Historical vs AI Predicted Days to Fill by Role
          </Typography>
        </Box>
        <Chip
          label="AI FORECAST"
          size="small"
          sx={{ bgcolor: "#EEF2FF", color: "#4F46E5", fontWeight: 700, fontSize: "11px", letterSpacing: "0.04em", borderRadius: "6px", mt: 0.25 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", height: CHART_H, justifyContent: "center", alignItems: "center" }}>
          <CircularProgress sx={{ color: AI_COLOR }} />
        </Box>
      ) : !hasData ? (
        <Box sx={{ height: CHART_H, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
          <img src={noTableData} width={60} alt="No Data" />
          <Typography mt={0.75} fontWeight={500} fontSize={18} fontFamily="'Noto Sans', sans-serif">
            No forecast data to show.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ overflowX: "auto", overflowY: "hidden", pb: 1, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 } }}>
            <svg
              viewBox={`0 0 ${chartWidth} ${CHART_H}`}
              style={{ width: chartWidth, height: CHART_H, display: "block", minWidth: chartWidth }}
              aria-label="Predictive time to hire chart"
            >
              {(() => {
                const { xOf, yOf, pointsOf, yMax, gridTicks } = buildChart(data, chartWidth);
                return (
                  <>
                    {/* Dashed horizontal grid lines + Y-axis labels */}
                    {gridTicks.map((t) => {
                      const y = yOf(yMax * t);
                      return (
                        <g key={t}>
                          <line
                            x1={PAD.left} x2={chartWidth - PAD.right}
                          y1={y} y2={y}
                          stroke="#E2E8F0" strokeWidth={1} strokeDasharray="4 4"
                        />
                        <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize={11} fill="#94A3B8">
                          {Math.round(yMax * t)}
                        </text>
                      </g>
                    );
                  })}

                  {/* Y-axis title */}
                  <text
                    x={12}
                    y={PAD.top + (CHART_H - PAD.top - PAD.bottom) / 2}
                    textAnchor="middle"
                    fontSize={11}
                    fill="#64748B"
                    transform={`rotate(-90, 12, ${PAD.top + (CHART_H - PAD.top - PAD.bottom) / 2})`}
                  >
                    Days to Fill
                  </text>

                  {/* X-axis title */}
                  <text
                    x={PAD.left + (chartWidth - PAD.left - PAD.right) / 2}
                    y={CHART_H - 4}
                    textAnchor="middle"
                    fontSize={11}
                    fill="#64748B"
                  >
                    Core Skill
                  </text>

                  {/* Historical Avg line — dashed gray */}
                  {data.length > 1 && (
                    <polyline
                      points={pointsOf("historicalAvg")}
                      fill="none"
                      stroke={HIST_COLOR}
                      strokeWidth={2}
                      strokeDasharray="6 4"
                    />
                  )}

                  {/* AI Predicted line — solid indigo */}
                  {data.length > 1 && (
                    <polyline
                      points={pointsOf("aiPredicted")}
                      fill="none"
                      stroke={AI_COLOR}
                      strokeWidth={2.5}
                    />
                  )}

                  {/* Data points + X-axis labels */}
                  {data.map((d, i) => (
                    <g key={d.skillId}>
                      {/* Historical Avg — hollow circle */}
                      <circle cx={xOf(i)} cy={yOf(d.historicalAvg)} r={5} fill="#fff" stroke={HIST_COLOR} strokeWidth={2} />
                      {/* AI Predicted — filled circle */}
                      <circle cx={xOf(i)} cy={yOf(d.aiPredicted)}   r={5} fill={AI_COLOR} stroke={AI_COLOR} />
                      {/* X-axis label */}
                      <text 
                        x={xOf(i)} 
                        y={CHART_H - PAD.bottom + 16} 
                        textAnchor="end" 
                        fontSize={11} 
                        fill="#64748B"
                        transform={`rotate(-35, ${xOf(i)}, ${CHART_H - PAD.bottom + 16})`}
                      >
                        {d.skillName.length > 15 ? d.skillName.substring(0, 15) + '...' : d.skillName}
                      </text>
                    </g>
                  ))}
                </>
              );
            })()}
          </svg>
          </Box>

          {/* Legend */}
          <Box sx={{ display: "flex", gap: 3, justifyContent: "center", mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <svg width={14} height={14}><circle cx={7} cy={7} r={5} fill={AI_COLOR} /></svg>
              <Typography variant="caption" color="text.secondary">AI Predicted</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <svg width={14} height={14}><circle cx={7} cy={7} r={5} fill="#fff" stroke={HIST_COLOR} strokeWidth={2} /></svg>
              <Typography variant="caption" color="text.secondary">Historical Avg</Typography>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default PredictiveTimeToHire;
